import { request, requestApiOk, requestFormDataApiOk } from './client';
import type { BillingInterval } from '@/generated/ts-rs/BillingInterval';
import type { BillingPortalResponse } from '@/generated/ts-rs/BillingPortalResponse';
import type { ChangePlanRequest } from '@/generated/ts-rs/ChangePlanRequest';
import type { CreateCheckoutRequest } from '@/generated/ts-rs/CreateCheckoutRequest';
import type { CreateCheckoutResponse } from '@/generated/ts-rs/CreateCheckoutResponse';
import type { ListPlansResponse } from '@/generated/ts-rs/ListPlansResponse';
import type { P12UploadData } from '@/generated/ts-rs/P12UploadData';
import type { PlanResponse } from '@/generated/ts-rs/PlanResponse';
import type { TenantProfileResponse } from '@/generated/ts-rs/TenantProfileResponse';
import type { UpdateProfileRequest } from '@/generated/ts-rs/UpdateProfileRequest';

export type ProfileResponse = TenantProfileResponse;

export function getProfile(token: string): Promise<TenantProfileResponse> {
  return request('GET', '/api/tenant/profile', undefined, token);
}

export type {
  BillingInterval,
  BillingPortalResponse,
  ChangePlanRequest,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  ListPlansResponse,
  P12UploadData,
  PlanResponse,
  TenantProfileResponse,
  UpdateProfileRequest,
};

/**
 * Upload the tenant AEAT P12 to the EXISTING Bearer endpoint
 * `POST /api/compliance/spain-verifactu/credentials/p12` — the same ingress
 * red_coral uses (INDEX C2, no new endpoint). One upload provisions the
 * compliance credential AND the production fiscal identity; cloud extracts
 * {tax_id, legal_name} from the certificate (authoritative, tamper-proof) and
 * returns them so the owner can confirm. Multipart field names mirror the
 * cloud handler: `p12_file` + `p12_password`.
 */
export function uploadFiscalP12(
  token: string,
  file: File,
  password: string,
): Promise<P12UploadData> {
  const form = new FormData();
  form.append('p12_file', file);
  form.append('p12_password', password);
  return requestFormDataApiOk<P12UploadData>(
    '/api/compliance/spain-verifactu/credentials/p12',
    form,
    token,
  );
}

/**
 * Atomic partial update of tenant profile. Backend accepts both fields
 * in a single PUT (Option<String> on each), so callers can update one or both.
 */
export function updateProfile(token: string, patch: UpdateProfileRequest): Promise<void> {
  return requestApiOk('PUT', '/api/tenant/profile', patch, token);
}

export function createBillingPortal(token: string): Promise<BillingPortalResponse> {
  return requestApiOk('POST', '/api/tenant/billing-portal', undefined, token);
}

export function createCheckout(
  token: string,
  plan_id: string,
  interval: BillingInterval,
): Promise<CreateCheckoutResponse> {
  const body: CreateCheckoutRequest = { plan_id, interval };
  return requestApiOk('POST', '/api/tenant/create-checkout', body, token);
}

export function cancelSubscription(token: string): Promise<void> {
  return requestApiOk('POST', '/api/tenant/cancel-subscription', undefined, token);
}

export function resumeSubscription(token: string): Promise<void> {
  return requestApiOk('POST', '/api/tenant/resume-subscription', undefined, token);
}

export function changePlan(
  token: string,
  plan_id: string,
  interval: BillingInterval,
): Promise<void> {
  const body: ChangePlanRequest = { plan_id, interval };
  return requestApiOk('POST', '/api/tenant/change-plan', body, token);
}

export function getPlans(token: string): Promise<ListPlansResponse> {
  return request('GET', '/api/tenant/plans', undefined, token);
}
